import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, BellOff, Undo2 } from "lucide-react";
import { toast } from "sonner";

export default function MutedBusinessesCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/unsubscribes");
      setItems(data.items || []);
    } catch (e) {
      console.warn("Unsubscribes load failed:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const resubscribe = async (bid, name) => {
    setBusy((b) => ({ ...b, [bid]: true }));
    try {
      await api.delete(`/me/unsubscribes/${bid}`);
      setItems((arr) => arr.filter((i) => i.business_id !== bid));
      toast.success(`Resubscribed to offers from ${name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy((b) => ({ ...b, [bid]: false }));
    }
  };

  return (
    <Card className="rounded-md border border-border p-6" data-testid="muted-businesses-card">
      <div className="flex items-center gap-2 flex-wrap">
        <BellOff size={18} className="text-[#B31E1E]" />
        <h3 className="font-display text-2xl">Muted promotional emails</h3>
      </div>
      <p className="text-sm text-gray-500 mt-1 max-w-2xl">
        Businesses you've unsubscribed from. You'll still receive promotional emails from every other business on Servrevu.
      </p>

      {loading ? (
        <p className="mt-5 text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <div className="mt-5 border border-dashed border-border rounded-md p-6 text-center" data-testid="muted-empty">
          <Bell size={22} className="mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            You're subscribed to promotional emails from all participating Servrevu businesses. Tap <strong>Unsubscribe</strong> in the footer of any promo email — or on a business profile — to mute that business individually.
          </p>
          <Link to="/directory" className="mt-3 inline-block text-sm font-medium text-[#B31E1E] hover:underline">Browse the directory →</Link>
        </div>
      ) : (
        <ul className="mt-5 space-y-2" data-testid="muted-list">
          {items.map((b) => (
            <li
              key={b.business_id}
              className="flex items-center justify-between gap-3 border border-border rounded-md px-3 py-2.5 bg-white"
              data-testid={`muted-row-${b.business_id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {b.business_logo ? (
                  <img
                    src={b.business_logo}
                    alt={`${b.business_name} logo`}
                    className="h-9 w-9 rounded-md border border-border object-contain bg-white shrink-0"
                  />
                ) : (
                  <div className="h-9 w-9 rounded-md bg-gray-100 border border-border shrink-0" />
                )}
                <div className="min-w-0">
                  <Link
                    to={`/businesses/${b.business_id}`}
                    className="text-sm font-medium text-gray-900 hover:underline truncate block"
                  >
                    {b.business_name}
                  </Link>
                  <Badge variant="outline" className="rounded-sm text-[10px] font-mono mt-0.5 text-gray-500">
                    Muted
                  </Badge>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => resubscribe(b.business_id, b.business_name)}
                disabled={!!busy[b.business_id]}
                className="rounded-[35px] shrink-0"
                data-testid={`muted-resubscribe-${b.business_id}`}
              >
                <Undo2 size={13} className="mr-1" /> {busy[b.business_id] ? "…" : "Resubscribe"}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
